import React, { useState, useEffect, useRef } from 'react';
import { connect, useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { displayDateTime } from 'reducers/TimeReducer';
import { closeAddressDialog, closeAddressSOP, showAddressSOP } from 'reducers/DialogsReducer';
import { getAddressSops, saveAddressSOP, deleteAddressSops } from 'reducers/AddressReducer';
import { showSpinner, hideSpinner } from 'reducers/UiReducer';
import { showDisplayDataWindow } from 'reducers/DialogsReducer';
import { handleError } from 'reducers/ErrorReducer';
import PrintsSearch from 'Search/components/PrintsSearch';
import { XGrid } from '@material-ui/x-grid';
import Tooltip from 'components/Tooltip';
import Fab from '@material-ui/core/Fab';
import InfoIcon from '@material-ui/icons/Info';
import { Button } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CheckIcon from '@material-ui/icons/Check';
import Dialog from 'components/Dialog';
import SaveIcon from '@material-ui/icons/Save';
import CloseIcon from '@material-ui/icons/Close';
import SOPFilter from './SOPFilter';
import DeleteIcon from '@material-ui/icons/Delete';

const bool = (params) => (params.value ? <CheckIcon /> : <span></span>);

const columns = [
  { field: 'SOPID', headerName: 'SOPID', width: 150 },
  { field: 'DispatcherMessage', headerName: 'Dispatcher Message', width: 130 },
  {
    field: 'Created',
    headerName: 'Created',
    width: 120,
    valueFormatter: (params) => displayDateTime(params.value),
    format: 'date',
  },
  { field: 'CreatedBy', headerName: 'Created By', width: 120 },
  {
    field: 'Updated',
    headerName: 'Updated',
    width: 120,
    valueFormatter: (params) => displayDateTime(params.value),
    format: 'date',
  },
  { field: 'UpdatedBy', headerName: 'Updated By', width: 120 },
  {
    field: 'IsDeleted',
    headerName: 'Is Deleted',
    width: 120,
    format: 'bool',
    renderCell: bool,
  },
  { field: 'DeletedBy', headerName: 'Deleted By', width: 120 },
  { field: 'DeletedIP', headerName: 'Deleted IP', width: 120 },
];

const useStyles = makeStyles((theme) => ({
  wrap: {
    padding: theme.spacing(3),
  },
  gridWrap: {
    width: '100%',
    boxSizing: 'border-box',
    height: 600,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(2),
    '& button': {
      marginLeft: theme.spacing(1),
    },
  },
}));

function AddressSOPs(props) {
  const { ptsAddressID, addAddressSOP, onSOPChange  } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const [data, setData] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [selectedSOP, setSelectedSOP] = useState(null);
  const [selection, setSelection] = useState(null);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const mountedRef = useRef(true);

  useEffect(() => {
    getSOPs();
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const getSOPs = async () => {
    try {
      const data = await getAddressSops(ptsAddressID);
      if (!mountedRef.current) return;
      setData(processData(data));
      onSOPChange?.(data.length);
      setLoaded(true);
    } catch (err) {
      props.handleError(err, 'Error receiving event location data.');
    }
  };

  const processData = (data) => {
    return data.map((row) => {
      if (row.IsDeleted === false) return { ...row, id: row.ROWGUID };
    });
  };

  const viewDetails = () => {
    const item = data.find((row) => row.id === selection);
    if (!item) return;
    props.showDisplayDataWindow({ data: filterData(item), title: item.SOPID });
  };

  const filterData = (rawData) => {
    const {
      SOPID,
      DispatcherMessage,
      AlertMessage,
      IsApproved,
      Approved,
      Flags,
      IsAlertMessageUsed,
      IsPagingMessageUsed,
      PagingMessage,
      ShowToast,
      CreatedBy,
      Updated,
      UpdatedBy,
      IsDeleted,
      DeletedBy,
      DeletedIP,
    } = rawData;
    return {
      SOPID,
      DispatcherMessage,
      AlertMessage,
      IsApproved,
      Approved,
      Flags,
      IsAlertMessageUsed,
      IsPagingMessageUsed,
      PagingMessage,
      ShowToast,
      CreatedBy,
      Updated,
      UpdatedBy,
      IsDeleted,
      DeletedBy,
      DeletedIP,
    };
  };

  const add = () => {
    dispatch(showAddressSOP({ ptsAddressId: ptsAddressID }));
  };

  const close = () => {
    dispatch(closeAddressSOP());
  };

  const save = async () => {
    dispatch(showSpinner());
    await saveAddressSOP({
      ptsParentID: ptsAddressID,
      ParentType: 'Address',
      ptsSOPID: selectedSOP,
    })
      .then(() => {
        close();
        getSOPs();
        dispatch(hideSpinner());
      })
      .catch(() => {
        close();
        getSOPs();
        dispatch(hideSpinner());
      });
  };

  const deleteSOP = async () => {
    if (!window.confirm('Are you sure you want to delete this record?')) return;
    const item = data.find((row) => row.id === selection);

    const updatedData = data.filter((row) => row.id !== selection);
    setData(updatedData);

    if (item) {
      dispatch(showSpinner());
      await deleteAddressSops({
        ptsParentID: ptsAddressID,
        ParentType: 'Address',
        ptsSOPID: item.ptsSOPID,
      })
        .then(() => {
          getSOPs();
          dispatch(hideSpinner());
        })
        .catch((err) => {
          console.log(err);
          getSOPs();
          dispatch(hideSpinner());
        });
    }
  };

  const formContent = () => {
    return <SOPFilter selectSOP={setSelectedSOP} />;
  };

  const renderActions = () => {
    return (
      <>
        <Button color="primary" variant="contained" autoFocus onClick={save}>
          <SaveIcon /> Save
        </Button>
        <Button color="primary" onClick={close}>
          <CloseIcon /> Close
        </Button>
      </>
    );
  };

  return (
    <div className={classes.wrap}>
      <div className={classes.actions}>
        <PrintsSearch title="SOP Records" data={data} cols={columns} hiddenCols={hiddenColumns} />
        <Tooltip title="Edit Attachment">
          <Fab size="small" color="secondary" onClick={add}>
            <AddIcon fontSize="small" />
          </Fab>
        </Tooltip>
        {selection !== null && (
          <>
            <Tooltip title="Delete SOP">
              <Fab size="small" color="secondary" onClick={deleteSOP}>
                <DeleteIcon />
              </Fab>
            </Tooltip>

            <Tooltip title="View details">
              <Fab size="small" color="secondary" onClick={viewDetails}>
                <InfoIcon />
              </Fab>
            </Tooltip>
          </>
        )}
      </div>

      {addAddressSOP && (
        <Dialog toolbar fullScreen onClose={close} title="Add SOP" actions={renderActions()}>
          {formContent()}
        </Dialog>
      )}

      <div className={classes.gridWrap}>
        <XGrid
          columns={columns}
          rows={data}
          loading={!loaded}
          rowHeight={38}
          disableMultipleSelection={true}
          showToolbar
          disableColumnFilter
          onSelectionModelChange={(newSelection) => {
            setSelection(newSelection.selectionModel[0]);
          }}
          onColumnVisibilityChange={(col) => setHiddenColumns([...hiddenColumns, col.field])}
        />
      </div>
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    addAddressSOP: state.dialogs.addAddressSOP,
  };
};

export default connect(mapStateToProps, {
  closeAddressDialog,
  handleError,
  showDisplayDataWindow,
})(AddressSOPs);
